import { gql } from "@apollo/client";

const SUBSCRIPTION_PROJECTS = gql`
  subscription Projects($where: project_list_view_bool_exp = {}) {
    project_list_func(where: $where) {
      invited_by
      name
      id
      vendor_id
      project_owner_subscription_name
    }
  }
`;

const SUBSCRIPTION_PROJECTS_PK = gql`
  subscription ProjectPK($id: uuid!) {
    project_by_pk(id: $id) {
      value
      client
      address
      number
      created_at
      created_by
      created_by_user {
        first_name
        last_name
      }
      details
      end_date
      id
      name
      start_date
      status_id
      spec_section
      updated_at
      updated_by
      mode
      timezone {
        timezone_id
        name
      }
    }
  }
`;

const SUBSCRIPTION_NEW_PROJECT_OFFSET_DEFAULT = gql`
  subscription GetNewProjectDefaultSetting(
    $where: workflow_defaults_bool_exp = {}
  ) {
    workflow_defaults(where: $where) {
      feature_id
      workflow_data
      float
    }
  }
`;

const SUBSCRIPTION_SUBMITTALS_LINKING = gql`
  subscription SubmittalsLinkings($ids: [uuid!]) {
    submittals(where: { id: { _in: $ids } }) {
      id
      submittal_id
      title
      description
      spec_name
      spec_no
      material_tracking
      status
      deleted_from_source
      submittal_schedule_links {
        linked_to_end_date
        created_at
        driving_task
        gantt_task {
          id
          source_task_id
          text
        }
      }
      submittal_material_links {
        created_at
        driving_material
        material {
          spec_section_name
          spec_section_no
          name
          material_id
          id
          actual_ROJ
          implicit
          material_schedule_links {
            linked_to_end_date
            reverse_dependency
            offset_duration
            linked_offset_date
            driving_task
            gantt_task {
              id
              source_task_id
              text
            }
          }
        }
      }
    }
  }
`;

const SUBSCRIPTION_MATERIAL_LINKING = gql`
  subscription MaterialLinking($ids: [uuid!]!) {
    material(where: { id: { _in: $ids } }) {
      spec_section_name
      spec_section_no
      name
      material_id
      id
      implicit
      submittal_material_links {
        created_at
        submittal {
          id
          submittal_id
          deleted_from_source
          title
          description
          spec_name
          spec_no
          workflow_status
          material_tracking
          submittal_schedule_links {
            driving_task
            gantt_task {
              id
              source_task_id
              text
            }
          }
        }
      }
      material_schedule_links {
        suggestion
        linked_to_end_date
        reverse_dependency
        created_at
        driving_task
        offset_duration
        linked_offset_date
        gantt_task {
          id
          source_task_id
          text
        }
      }
    }
  }
`;

const SUBSCRIPTION_MARKUPS_LAYER_PK = gql`
  subscription MySubscription($id: uuid!) {
    attachments_by_pk(id: $id) {
      markup_layers {
        id
        markups {
          id
          data
          page_no
          type
          created_at
          created_by_user {
            first_name
            last_name
          }
        }
      }
    }
  }
`;

const SUBSCRIPTION_PROJECT_USERS = gql`
  subscription ProjectUsers($where: project_users_bool_exp) {
    project_users(where: $where) {
      user_id
      subscription_id
      user_type {
        id
        name
      }
      user {
        email
        first_name
        id
        status_id
        last_name
        phone
      }
      project_role {
        id
        key
        name
      }
      subscription_vendor {
        id
        name
        partner_subscription_id
        project_vendors {
          type_id
          project_vendors_company_status {
            status
          }
          project_vendors_type {
            key
            name
            id
          }
        }
      }
      subscription {
        organization_subscriptions {
          organization {
            name
          }
        }
      }
      status_id
      external
    }
  }
`;

const SUBSCRIPTION_PROJECT_USERS_STREAM = gql`
  subscription ProjectUsersStream(
    $where: project_users_bool_exp
    $cursor: [project_users_stream_cursor_input]!
  ) {
    project_users_stream(batch_size: 100, cursor: $cursor, where: $where) {
      user_id
      subscription_id
      user_type {
        id
        name
      }
      user {
        email
        first_name
        id
        status_id
        last_name
        phone
      }
      project_role {
        id
        key
        name
      }
      subscription_vendor {
        id
        name
        partner_subscription_id
        project_vendors {
          type_id
          project_vendors_company_status {
            status
          }
          project_vendors_type {
            key
            name
            id
          }
        }
      }
      subscription {
        organization_subscriptions {
          organization {
            name
            id
          }
        }
      }
      status_id
      external
      external_source_id
      project_integration_users {
        full_source_name
      }
      subscription_user {
        user_source_infos {
          full_source_name
        }
      }
    }
  }
`;

const SUBSCRIPTION_PROJECT_VENDORS = gql`
  subscription ProjectQuery($where: project_vendors_bool_exp = {}) {
    project_vendors(where: $where) {
      vendor_id
      subscription_vendor {
        partner_subscription_id
        name
        project_users {
          external
        }
        vendor_source_infos {
          full_source_name
        }
      }
      type_id
      project_vendors_type {
        id
        name
        key
      }
      status
      project_vendors_company_status {
        status
        id
      }
      external_source_id
    }
  }
`;

const SUBSCRIPTION_ACTIVE_PROJECT_USERS = gql`
  subscription ProjectUsers($where: project_users_bool_exp!) {
    project_users(where: $where) {
      status_id
      user {
        email
        first_name
        id
        status_id
        last_name
        phone
      }
    }
  }
`;

const SUBSCRIPTION_USERS = gql`
  subscription subscription_users {
    subscription_users {
      user_id
      user {
        id
        first_name
        last_name
        email
        phone
      }
      subscription_permission_id
      subscription_permission {
        name
      }
      subscription {
        organization_subscriptions {
          organization {
            name
            id
          }
        }
      }
      status_id
      vendor_id
      external_source_id
      subscription_vendor {
        id
        name
      }
      user_source_infos {
        full_source_name
      }
    }
  }
`;

const SUBSCRIPTION_USERS_ACTIVE = gql`
  subscription subscription_users {
    subscription_users(where: { status_id: { _nin: [3, 4] } }) {
      user_id
      user {
        id
        first_name
        last_name
        email
        phone
      }
      subscription_permission_id
      subscription_permission {
        name
      }
      subscription {
        organization_subscriptions {
          organization {
            name
          }
        }
      }
      status_id
      vendor_id
      external_source_id
      subscription_vendor {
        id
        name
      }
      user_source_infos {
        full_source_name
      }
    }
  }
`;

const SUBSCRIPTION_PROJECT_ROLES = gql`
  subscription ProjectRole($where: project_role_bool_exp) {
    project_role(where: $where) {
      id
      name
      type_id
      key
    }
  }
`;

const SUBSCRIPTION_USER_SUBSCRIPTION_ROLES = gql`
  subscription UserSubscriptionRole(
    $where: subscription_permission_bool_exp = {}
  ) {
    subscription_permission(where: $where) {
      id
      name
      key
    }
  }
`;

const SUBSCRIPTION_USER_PROFILE_INFO = gql`
  subscription UserProfileInfo($where: subscription_users_bool_exp) {
    subscription_users(where: $where) {
      user {
        first_name
        last_name
        email
        phone
      }
      subscription {
        default_vendor_id
        organization_subscriptions {
          organization {
            name
          }
        }
      }
      user_source_infos {
        full_source_name
      }
    }
  }
`;

const SUBSCRIPTION_COMPANY_LIST = gql`
  subscription CompanyList($where: subscription_vendors_bool_exp = {}) {
    subscription_vendors(where: $where, order_by: { name: asc }) {
      id
      name
      address
      external_source_id
      trade_partner_pocs(where: { deleted: { _eq: false } }) {
        email
        first_name
        last_name
        phone
        id
      }
      project_vendors {
        type_id
        project_vendors_company_status {
          status
        }
      }
      vendor_source_infos {
        full_source_name
      }
    }
  }
`;

const SUBSCRIPTION_LEVEL_COMPANY_LIST = gql`
  subscription CompanyList($where: subscription_vendors_bool_exp = {}) {
    subscription_vendors(where: $where, order_by: { name: asc }) {
      id
      name
      address
      external_source_id
      trade_partner_pocs(where: { deleted: { _eq: false } }) {
        email
        first_name
        last_name
        phone
        id
      }
      vendor_source_infos {
        full_source_name
      }
    }
  }
`;

const SUBSCRIPTION_ATTACHMENT_MARKUP_REF = gql`
  subscription MyQuery($reference_id: uuid!) {
    attachments(where: { reference_id: { _eq: $reference_id } }) {
      id
    }
  }
`;

const SUBSCRIPTION_CALENDAR_LIST = gql`
  subscription CalendarLis($projectId: uuid) {
    gantt_calendar(
      where: { deleted: { _eq: false }, project_id: { _eq: $projectId } }
      order_by: { created_at: desc }
    ) {
      id
      name
      description
      calendar_holiday_lists(where: { deleted: { _eq: false } }) {
        id
        country_code
        holiday_name
        date
      }
      work_shifts {
        id
        hours
        name
      }
      calendar_shift_mapping {
        id
        deleted
        work_day
        work_day_mapping {
          name
        }
        work_shift {
          name
          hours
        }
      }
    }
  }
`;

const SUBSCRIPTION_DISTRIBUTION_GROUP_LIST = gql`
  subscription DistributionGroups($where: distribution_group_bool_exp!) {
    distribution_group(where: $where) {
      name
      id
      distribution_group_user {
        user {
          id
          email
          first_name
          last_name
          project_users {
            status_id
            project_role {
              name
            }
            subscription {
              organization_subscriptions {
                organization {
                  name
                }
              }
            }
            subscription_vendor {
              name
            }
          }
        }
      }
    }
  }
`;

const SUBSCRIPTION_GET_SCHEDULE_VERSIONS = gql`
  subscription ScheduleVersions {
    schedule_import_details(
      order_by: { created_at: desc }
      where: { import_log: { import_type: { _eq: SCHEDULE } } }
    ) {
      id
      import_log_id
      is_active
      schedule_name
      schedule_date
      schedule_type
      activated_at
      created_at
      import_log {
        id
        resolved_values_s3_prefix
        source_values_s3_prefix
        created_at
        created_by_user {
          id
          first_name
          last_name
        }
      }
    }
  }
`;

const SUBSCRIPTION_IMPORT_FILE_PROCESSING = gql`
  subscription SubscribeToImportLog($where: import_log_bool_exp!) {
    import_log(where: $where) {
      status
      status_comment
    }
  }
`;

const SUBSCRIPTION_SC_PROJECT_DASHBOARD = gql`
  subscription ProjectDashboard {
    project_sc_dashboard_func {
      count
      item_type
      status
    }
  }
`;

const SUBSCRIPTION_ARCH_PROJECT_DASHBOARD = gql`
  subscription ProjectDashboard {
    project_arch_dashboard_func {
      count
      item_type
      status
    }
  }
`;

const SUBSCRIPTION_LATEST_SCHED_IMPORTS = gql`
  subscription LatestScheduleImportDetails {
    schedule_import_details(order_by: { id: desc }) {
      created_at
      schedule_name
      schedule_date
      is_active
      activated_at
      import_log {
        id
      }
    }
  }
`;

const SUBSCRIPTION_SCHEDULE_ALERT = gql`
  subscription ScheduleAlertDetails {
    gantt_tasks_aggregate(
      where: {
        _and: [
          {
            _or: [
              {
                material_schedule_links_aggregate: {
                  count: { predicate: { _gt: 0 } }
                }
              }
              {
                submittal_schedule_links_aggregate: {
                  count: { predicate: { _gt: 0 } }
                }
              }
            ]
          }
          { latest_imported_schedule_impact: { _is_null: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

const SUBSCRIPTION_TASK_DETAIL_BY_TASK_ID = gql`
  subscription SUBSCRIPTION_TASK_DETAIL_BY_TASK_ID($id: uuid!) {
    gantt_tasks_by_pk(id: $id) {
      active
      assignee
      duration
      end_date
      status
      text
      type
      progress
      start_date
    }
  }
`;

const SUBSCRIPTION_PROJECT_INTEGRATION = gql`
  subscription GetProjectIntegrationKey {
    external_integrations {
      integration_key
      configuration
    }
  }
`;

const SUBSCRIPTION_SPEC_PAGE_EXTRACTS = gql`
  subscription SpecPageExtractsQuery {
    spec_page_extracts {
      id
      bb_x1
      bb_x2
      bb_y1
      bb_y2
      page_no
      metadata
      spec_page {
        id
        width
        height
        page_file_key
        specification {
          title
          spec_text_analysis_stat {
            spec_file_key
          }
        }
      }
      materials_aggregate {
        aggregate {
          count
        }
      }
      feature_materials_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

const SUBSCRIPTION_PROJECT_COMPANY_LIST = gql`
  subscription ProjectCompanyListQuery {
    project_vendors {
      vendor_id
      type_id
      subscription_vendor {
        name
        id
      }
    }
  }
`;

const NEW_SUBSCRIPTION_SUBMITTALS_LINKING = gql`
  subscription getSubmittalDetails($feature_instance_id: [uuid!]) {
    submittals: feature_instance(where: { id: { _in: $feature_instance_id } }) {
      id
      submittal_id: auto_inc_value
      title
      description
      feature_instance_spec_sections {
        spec_section {
          spec_no: section_number
          spec_name: title
        }
      }
      feature_submittals {
        material_tracking
        status
        submittal_type {
          id
          value
        }
      }
      deleted_from_source
      submittal_schedule_links: links_as_source(
        where: { target_feature_instance: { feature_type_id: { _eq: 9 } } }
      ) {
        link_type
        governing_link
        target_feature_instance {
          id
          source_task_id: number
          text: title
          feature_type_id
          feature_activities {
            duration
            end_date
            start_date
            import_log_id
            parent
            critical
            is_critical
            total_slack
            total_slack_computed
            progress
            actual_start_date
            actual_finish_date
          }
        }
      }
      submittal_material_links: links_as_source(
        where: { target_feature_instance: { feature_type_id: { _eq: 2 } } }
      ) {
        driving_material: governing_link
        target_feature_instance {
          id
          implicit
          material_id: auto_inc_value
          name: title
          feature_type_id
          material_schedule_links: links_as_source(
            where: { target_feature_instance: { feature_type_id: { _eq: 9 } } }
          ) {
            governing_link
            link_type
            offset_duration
            linked_offset_date
            reverse_dependency
            target_feature_instance {
              id
              source_task_id: number
              text: title
            }
          }
          schedule_material_links: links_as_target(
            where: {
              _or: [
                { source_feature_instance: { feature_type_id: { _eq: 9 } } }
              ]
            }
          ) {
            governing_link
            created_at
            link_type
            offset_duration
            linked_offset_date
            reverse_dependency
            target_feature_instance: source_feature_instance {
              id
              source_task_id: number
              text: title
            }
          }
          feature_workflow_instance {
            risk_level
            milestone_state
          }
          feature_instance_spec_sections {
            spec_section {
              section_number
              section_name: title
              status
            }
          }
        }
      }
    }
  }
`;

const NEW_SUBSCRIPTION_MATERIAL_LINKING = gql`
  subscription getMaterialDetails($feature_instance_id: [uuid!]) {
    materials: feature_instance(where: { id: { _in: $feature_instance_id } }) {
      id
      material_id: auto_inc_value
      title
      description
      feature_instance_spec_sections {
        spec_section {
          spec_no: section_number
          spec_name: title
        }
      }
      feature_materials {
        status
      }
      deleted_from_source
      material_schedule_links: links_as_source(
        where: { target_feature_instance: { feature_type_id: { _eq: 9 } } }
      ) {
        governing_link
        created_at
        link_type
        offset_duration
        linked_offset_date
        reverse_dependency
        target_feature_instance {
          id
          source_task_id: number
          text: title
          feature_activities {
            type
          }
        }
      }
      schedule_material_links: links_as_target(
        where: {
          _or: [{ source_feature_instance: { feature_type_id: { _eq: 9 } } }]
        }
      ) {
        governing_link
        created_at
        link_type
        offset_duration
        linked_offset_date
        reverse_dependency
        target_feature_instance: source_feature_instance {
          id
          source_task_id: number
          text: title
        }
      }
      submittal_material_links: links_as_target(
        where: { source_feature_instance: { feature_type_id: { _eq: 1 } } }
      ) {
        source_feature_instance {
          id
          submittal_id: auto_inc_value
          title
          feature_type_id
          revision
          deleted_from_source
          description
          feature_submittals {
            workflow_status
            material_tracking
          }
          feature_instance_spec_sections {
            spec_section {
              section_name: title
              section_number
            }
          }
          submittal_schedule_links: links_as_source(
            where: { target_feature_instance: { feature_type_id: { _eq: 9 } } }
          ) {
            link_type
            offset_duration
            linked_offset_date
            target_feature_instance {
              id
              source_task_id: number
              text: title
            }
          }
          feature_workflow_instance {
            risk_level
            milestone_state
          }
        }
      }
    }
  }
`;

export {
  SUBSCRIPTION_PROJECTS,
  SUBSCRIPTION_PROJECTS_PK,
  SUBSCRIPTION_PROJECT_USERS,
  SUBSCRIPTION_PROJECT_USERS_STREAM,
  SUBSCRIPTION_PROJECT_VENDORS,
  SUBSCRIPTION_USERS,
  SUBSCRIPTION_USERS_ACTIVE,
  SUBSCRIPTION_PROJECT_ROLES,
  SUBSCRIPTION_USER_SUBSCRIPTION_ROLES,
  SUBSCRIPTION_USER_PROFILE_INFO,
  SUBSCRIPTION_MARKUPS_LAYER_PK,
  SUBSCRIPTION_COMPANY_LIST,
  SUBSCRIPTION_ATTACHMENT_MARKUP_REF,
  SUBSCRIPTION_CALENDAR_LIST,
  SUBSCRIPTION_DISTRIBUTION_GROUP_LIST,
  SUBSCRIPTION_ACTIVE_PROJECT_USERS,
  SUBSCRIPTION_GET_SCHEDULE_VERSIONS,
  SUBSCRIPTION_IMPORT_FILE_PROCESSING,
  SUBSCRIPTION_SC_PROJECT_DASHBOARD,
  SUBSCRIPTION_ARCH_PROJECT_DASHBOARD,
  SUBSCRIPTION_LATEST_SCHED_IMPORTS,
  SUBSCRIPTION_SCHEDULE_ALERT,
  SUBSCRIPTION_SUBMITTALS_LINKING,
  SUBSCRIPTION_MATERIAL_LINKING,
  SUBSCRIPTION_LEVEL_COMPANY_LIST,
  SUBSCRIPTION_TASK_DETAIL_BY_TASK_ID,
  SUBSCRIPTION_PROJECT_INTEGRATION,
  SUBSCRIPTION_NEW_PROJECT_OFFSET_DEFAULT,
  SUBSCRIPTION_SPEC_PAGE_EXTRACTS,
  SUBSCRIPTION_PROJECT_COMPANY_LIST,
  NEW_SUBSCRIPTION_SUBMITTALS_LINKING,
  NEW_SUBSCRIPTION_MATERIAL_LINKING
};
